import api from 'src/utils/ajax';

import { Identifier } from '../../types/utilityTypes';
import { Platform } from '../platform/_types';

import {
  FetchPlatformNodesOptions,
  PlatformHierarchyNode,
  PlatformNode,
  UpdatePlatformNodeOptions,
} from './_types';
import {
  denormalizePlatformHierarchyNode,
  denormalizePlatformNode,
  normalizePlatformHierarchyNode,
  normalizePlatformNode,
} from './normalizers';

export * from './_types';

export const platformNodesQueryKey = 'platformNodes';
export const platformHierarchyNodesQueryKey = 'platformHierarchyNodes';

/**
 * Fetch platform nodes with optional filtering
 */
export const fetchPlatformNodes = async (
  options: FetchPlatformNodesOptions = {}
): Promise<{ nodes: PlatformNode[]; total: number }> => {
  const { search, includeHierarchyNodes, filters, fetchAll } = options;
  const params: Record<string, any> = {};
  if (search) params.search = search;
  if (fetchAll) params.fetch_all = true;
  const response = await api.get('/api/v4/fbm/platform_nodes', {
    params: {
      ...params,
      q: {
        ...(filters?.terminalNodes ? { terminal_nodes: true } : {}),
      },
    },
  });
  let nodes: PlatformNode[] = [];
  if (response.data.platform_node) {
    nodes = response.data.platform_node.map(normalizePlatformNode);
  } else {
    nodes = response.data.map(normalizePlatformNode);
  }

  // If includeHierarchyNodes is true, also fetch and populate the hierarchy nodes
  let hierarchyNodesMap: Record<string, PlatformHierarchyNode> = {};
  if (includeHierarchyNodes && nodes.length > 0) {
    const hierarchyNodes = await fetchPlatformHierarchyNodes({
      fetchAll: true,
    });

    // Create a map for quick lookup
    hierarchyNodesMap = hierarchyNodes.hierarchyNodes.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {} as Record<string, PlatformHierarchyNode>);

    // Populate the hierarchyNode field
  }
  nodes.forEach((node) => {
    if (node.fbmPlatformHierarchyNodeId && hierarchyNodesMap) {
      node.hierarchyNode = hierarchyNodesMap[node.fbmPlatformHierarchyNodeId];
    }
  });

  if (fetchAll) {
    return { nodes, total: nodes.length };
  }
  return { nodes, total: response.data.meta.total };
};

export const createPlatformNodesFromTree = async (
  nodes: PlatformNode[]
): Promise<PlatformNode[]> => {
  const response = await api.post('/api/v4/fbm/platform_nodes/save_tree', {
    nodes,
  });
  return response.data.map(normalizePlatformNode);
};

/**
 * Create a platform node tree
 */
export const BuildPlatformNodeTree = (
  nodes: PlatformNode[]
): PlatformNode[] => {
  // Build the node tree structure
  const rootNodes: PlatformNode[] = [];
  const nodeMap: Record<string, PlatformNode> = {};

  // First create a map of all nodes
  nodes.forEach((node) => {
    nodeMap[String(node.id)] = { ...node, children: [] };
  });

  // Then build the tree structure
  nodes.forEach((node) => {
    if (node.parentId && nodeMap[String(node.parentId)]) {
      if (!nodeMap[String(node.parentId)].children) {
        nodeMap[String(node.parentId)].children = [];
      }
      nodeMap[String(node.parentId)].children?.push(nodeMap[String(node.id)]);
    } else {
      rootNodes.push(nodeMap[String(node.id)]);
    }
  });
  return rootNodes;
};

export const filterPlatformsBySearchTerm = (
  platforms: Platform[],
  searchTerm: string
): Platform[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return platforms;
  }
  return platforms.filter(
    (platform) =>
      platform.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      platform.trainerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      platform.trainerEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

/**
 * Filters platform nodes to only include those that match the search term or have
 * descendants (including platforms) that match the search term.
 *
 * @param nodes The complete list of platform nodes
 * @param platforms The list of platforms to check for matches
 * @param searchTerm The search term to filter by
 * @returns Filtered list of root nodes with only matching branches
 */
export const filterSitesBySearchTerm = (
  nodes: PlatformNode[],
  platforms: Platform[],
  searchTerm: string
): PlatformNode[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return BuildPlatformNodeTree(nodes);
  }

  // Find nodes that match directly or have matching platforms
  const matchingNodes = new Set<string>();
  const matchingPlatforms = new Set<string>();

  // Check if platforms match the search term
  filterPlatformsBySearchTerm(platforms, searchTerm).forEach((platform) => {
    // Remember matching platforms
    matchingPlatforms.add(String(platform.id));

    // If platform matches, add its parent node ID to the matching set
    if (platform.fbmPlatformNodeId) {
      matchingNodes.add(String(platform.fbmPlatformNodeId));
    }
  });

  // Create a map of all nodes for easy access
  const nodeMap: Record<string, PlatformNode> = {};
  nodes.forEach((node) => {
    nodeMap[String(node.id)] = { ...node, children: [] };
  });

  // Add all parent nodes of matching nodes to the matching set
  const addAllParents = (nodeId: string) => {
    const node = nodeMap[nodeId];
    if (node && node.parentId) {
      matchingNodes.add(String(node.parentId));
      addAllParents(String(node.parentId));
    }
  };

  // For each matching node, add all its parent nodes to the matching set
  Array.from(matchingNodes).forEach((nodeId) => {
    addAllParents(nodeId);
  });

  // We'll keep the complete tree structure but only include matching platforms
  const terminalLevel = Math.max(
    ...nodes
      .filter((n) => n.hierarchyNode)
      .map((n) => n.hierarchyNode?.level || 0)
  );

  // Filter nodes to include all that could lead to a matching platform
  // This preserves the full path to matching platforms
  const filteredNodes = nodes.filter((node) => {
    // Keep all nodes that match directly
    if (matchingNodes.has(String(node.id))) {
      return true;
    }

    // For terminal nodes, we need to be more selective
    if (node.hierarchyNode?.level === terminalLevel) {
      // Check if this terminal node has any matching platforms as children
      const hasMatchingPlatforms = platforms.some(
        (p) =>
          p.fbmPlatformNodeId === node.id && matchingPlatforms.has(String(p.id))
      );
      return hasMatchingPlatforms;
    }

    return false;
  });

  // Build the filtered tree
  return filteredNodes;
};

/**
 * Fetch a single platform node by ID
 */
export const fetchPlatformNode = async (
  id: Identifier
): Promise<PlatformNode> => {
  const response = await api.get(`/api/v4/fbm/platform_nodes/${id}`);
  return normalizePlatformNode(response.data);
};

/**
 * Create a new platform node
 */
export const createPlatformNode = async (
  node: Partial<PlatformNode>
): Promise<PlatformNode> => {
  const data = denormalizePlatformNode(node);
  const response = await api.post('/api/v4/fbm/platform_nodes', {
    platform_node: data,
  });
  return normalizePlatformNode(response.data);
};

/**
 * Update an existing platform node
 */
export const updatePlatformNode = async (
  id: Identifier,
  options: UpdatePlatformNodeOptions
): Promise<PlatformNode> => {
  const data = denormalizePlatformNode(options);
  const response = await api.put(`/api/v4/fbm/platform_nodes/${id}`, {
    platform_node: data,
  });
  return normalizePlatformNode(response.data);
};

/**
 * Delete a platform node
 */
export const deletePlatformNode = async (
  id: Identifier
): Promise<PlatformNode> => {
  const response = await api.delete(`/api/v4/fbm/platform_nodes/${id}`);
  return normalizePlatformNode(response.data);
};

/**
 * Fetch platform hierarchy nodes with optional filtering
 */
export const fetchPlatformHierarchyNodes = async (
  options: { level?: number; fetchAll?: boolean } = {}
): Promise<{ hierarchyNodes: PlatformHierarchyNode[]; total: number }> => {
  const { level, fetchAll } = options;

  const params: Record<string, any> = {};
  if (level !== undefined) params.level = level;
  if (fetchAll) params.fetch_all = true;

  const response = await api.get('/api/v4/fbm/platform_hierarchy_nodes', {
    params,
  });
  let hierarchyNodes: {
    hierarchyNodes: PlatformHierarchyNode[];
    total: number;
  } = {
    hierarchyNodes: [],
    total: 0,
  };
  if (response.data.platform_hierarchy_node) {
    hierarchyNodes = {
      hierarchyNodes: response.data.platform_hierarchy_node.map(
        normalizePlatformHierarchyNode
      ),
      total: response.data.meta.total,
    };
  } else {
    hierarchyNodes = {
      hierarchyNodes: response.data.map(normalizePlatformHierarchyNode),
      total: 0,
    };
  }
  return hierarchyNodes;
};

/**
 * Fetch a single platform hierarchy node by ID
 */
export const fetchPlatformHierarchyNode = async (
  id: Identifier
): Promise<PlatformHierarchyNode> => {
  const response = await api.get(`/api/v4/fbm/platform_hierarchy_nodes/${id}`);
  return normalizePlatformHierarchyNode(response.data);
};

/**
 * Create a new platform hierarchy node
 */
export const createPlatformHierarchyNode = async (
  node: Partial<PlatformHierarchyNode>
): Promise<PlatformHierarchyNode> => {
  const data = denormalizePlatformHierarchyNode(node);
  const response = await api.post('/api/v4/fbm/platform_hierarchy_nodes', {
    platform_hierarchy_node: data,
  });
  return normalizePlatformHierarchyNode(response.data);
};

/**
 * Append a platform hierarchy node
 */
export const appendPlatformHierarchyNode = async (
  node: Partial<PlatformHierarchyNode>
): Promise<PlatformHierarchyNode> => {
  const data = denormalizePlatformHierarchyNode(node);
  const response = await api.post(
    '/api/v4/fbm/platform_hierarchy_nodes/append_platform_hierarchy_node',
    {
      platform_hierarchy_node: data,
    }
  );
  return normalizePlatformHierarchyNode(response.data);
};

/**
 * Update an existing platform hierarchy node
 */
export const updatePlatformHierarchyNode = async (
  id: Identifier,
  node: Partial<PlatformHierarchyNode>
): Promise<PlatformHierarchyNode> => {
  const data = denormalizePlatformHierarchyNode(node);
  const response = await api.put(`/api/v4/fbm/platform_hierarchy_nodes/${id}`, {
    platform_hierarchy_node: data,
  });
  return normalizePlatformHierarchyNode(response.data);
};

/**
 * Delete a platform hierarchy node
 */
export const deletePlatformHierarchyNode = async (
  id: Identifier
): Promise<PlatformHierarchyNode> => {
  const response = await api.delete(
    `/api/v4/fbm/platform_hierarchy_nodes/${id}`
  );
  return normalizePlatformHierarchyNode(response.data);
};
